// Report Functions

// Documentar
// Global constants
import * as con from "../GlobalConstants"

import axios from 'axios';
import { extractErrorMessage, handleRequestError } from "./errorFunctions";

import { getFinanceRequestConfig, getRequestConfig, getSurrogateUser, getUser, getVisualizedUser, surrogateActive } from "../store/logic/supportFunctions";
import { downloadFileFromResponse } from "./downloadFunctions";
import { createNotification } from "../templates/notifications/Notifications";
import { formatDate, getToday } from "./dateFunctions";
import { setApplicationParameter } from "../store/slices/applicationParametersSlice";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { processTransactionsWithSummary } from "./transactionFunctions";
import { DateTime } from 'luxon';

// =====================================================
// MAKE PURCHASE REPORT
// =====================================================

export const makePurchaseReport = createAsyncThunk(
  "applicationParameters/makePurchaseReport",
  async ({ startDate, endDate, includePurchases, operation }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      
      let data = {};
      
      // Model Parameters
      data[con.REDUCER_MODEL_PARAMETERS] = state[con.REDUCER_APPLICATION_PARAMETERS][con.REDUCER_MODEL_PARAMETERS];
      
      // Forward Coverages
      data[con.REDUCER_FWD_COVERAGES] = Object.values(state[con.REDUCER_FWD_COVERAGES] || {});
      
      // Options Coverages
      data[con.REDUCER_OPTION_COVERAGES] = Object.values(state[con.REDUCER_OPTION_COVERAGES] || {});
      
      // Spot Coverages
      data[con.REDUCER_SPOT_COVERAGES] = Object.values(state[con.REDUCER_SPOT_COVERAGES] || {});
      
      // Start date
      data[con.START_DATE] = startDate;
      
      // endDate
      data[con.END_DATE] = endDate;
      
      // Include purchases
      data[con.INCLUDE_PURCHASES] = includePurchases;
      
      // Operation
      data[con.OPERATION] = operation;

      data[con.CLIENT_TYPE] = state[con.REDUCER_MODEL_PARAMETERS][con.CLIENT_TYPE];
      data[con.USER_LINE] = state[con.REDUCER_MODEL_PARAMETERS][con.USER_LINE];
      
      // Surrogate user logic
      let url = con.rest_base_url + con.reports_api + con.purchase_reporte_url;
      if(surrogateActive()) {
        data[con.SURROGATE_USER] = getSurrogateUser()[con.ID];
        url = con.rest_base_url + con.reports_api + con.STAFF_API + con.purchase_reporte_url;
      }
      
      const config = getRequestConfig();
      config.responseType = 'blob';
      
      const resp = await axios.post(url, data, config);
      
      downloadFileFromResponse(resp, constructFileName(con.PURCHASE_REPORT_NAME, con.PDF_FILE_TYPE));
      
      return resp.data;
      
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error generando el reporte de compras"));
    }
  }
);

// =====================================================
// MAKE POSITION REPORT
// =====================================================
export const makePositionReport = createAsyncThunk(
  "applicationParameters/makePositionReport",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const { modelApplicationParameters, volatility, scenario } = payload;
      const state = getState();
      const transactionsState = state.transactions;
      const applicationParameters = state[con.REDUCER_APPLICATION_PARAMETERS];
      const scenariosState = state[con.REDUCER_SCENARIOS];
      const scenarios = scenariosState.byId || {};
      
      let data = {};
       
      data[con.REDUCER_SCENARIOS] = scenarios;

      const selectedScenarioId = applicationParameters[con.SELECTED_SCENARIO]
    
      // Model Parameters
      data[con.REDUCER_MODEL_PARAMETERS] = state[con.REDUCER_MODEL_PARAMETERS];
      
      // Adds missing values desde applicationParameters
      data[con.REDUCER_MODEL_PARAMETERS] = {
        ...state[con.REDUCER_MODEL_PARAMETERS],
        [con.SELECTED_SCENARIO]: selectedScenarioId,
        [con.SPOT]: applicationParameters[con.SPOT]
      };

      data[con.REDUCER_MODEL_PARAMETERS][con.MARKET_VOLATILITY] = volatility

      // Exposures
      const exposures = Object.values(transactionsState?.byType?.exposures ||{});
      
      // Forward Coverages
      const fwd_coverages = Object.values(transactionsState?.byType?.forward_coverages || {});
      
      // Options Coverages
      const options_coverages = Object.values(transactionsState?.byType?.option_coverages || {});
      
      // Accounts
      data[con.REDUCER_ACCOUNTS] = Object.values(transactionsState?.byType?.accounts || {});

      let currentDate = applicationParameters[con.CURRENT_DATE];
      if (typeof currentDate === 'string') {
        currentDate = DateTime.fromISO(currentDate);
      }

      const spot = parseFloat(applicationParameters[con.SPOT]);

      let transSummary = await processTransactionsWithSummary(
        currentDate,
        spot,
        scenario,
        data[con.REDUCER_MODEL_PARAMETERS],
        exposures,
        fwd_coverages,
        options_coverages,
        data[con.REDUCER_ACCOUNTS],
        modelApplicationParameters
      );
      
      data[con.TRANSACTION_SUMMARY] = transSummary;
      
      // Surrogate user logic
      let url = con.rest_base_url + con.reports_api + con.position_reporte_url;
      if(surrogateActive()) {
        data[con.SURROGATE_USER] = getSurrogateUser()[con.ID];
        url = con.rest_base_url + con.reports_api + con.STAFF_API + con.position_reporte_url;
      }
      
      const config = getRequestConfig();
      config.responseType = 'blob';
      
      const resp = await axios.post(url, data, config);
      
      downloadFileFromResponse(resp, constructFileName(con.POSITION_REPORT_NAME, con.PDF_FILE_TYPE));
      
      return resp.data;
      
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error generando el reporte de posición"));
    }
  }
);
export const makeForwardAssessmentReport = (assessmentSummary, assessedFwdArray) => async (dispatch, getState) => {
    
    dispatch(setApplicationParameter({ 
        id: con.FORWARD_ASSESSMENT_REPORT, 
        value: { [con.GENERATING]: true, [con.REPORT_FILE_NAME]: null } 
    }))
    
    const applicationParameters = getState()[con.REDUCER_APPLICATION_PARAMETERS]

    let data = {}

    // Forward Coverages (with assessment)
    data[con.REDUCER_FWD_COVERAGES] = assessedFwdArray

    // Forward Coverages Summary
    data[con.SUMMARY] = assessmentSummary

    // Adds Parameters
    data[con.PARAMETERS] = {
        [con.FORWARD_ASSESSMENT_SHOW_EXPIRED_COVERAGES]: applicationParameters[con.FORWARD_ASSESSMENT_SHOW_EXPIRED_COVERAGES],
        [con.FORWARD_ASSESSMENT_REFERENCE_DATE]: applicationParameters[con.FORWARD_ASSESSMENT_REFERENCE_DATE],
        [con.FORWARD_ASSESSMENT_START_DATE]: applicationParameters[con.FORWARD_ASSESSMENT_START_DATE],
        [con.USER]: surrogateActive() ? getSurrogateUser()[con.ID] : getUser()[con.ID]
    }

    const url = con.rest_finance_url + con.reports_finance + con.forward_assessment_report_url
    const config = getFinanceRequestConfig()
    config.responseType = 'blob'

    try {
        const resp = await axios.post(url, data, config)
        dispatch(setApplicationParameter({ 
            id: con.FORWARD_ASSESSMENT_REPORT, 
            value: { [con.GENERATING]: false } 
        }))
        downloadFileFromResponse(resp, constructFileName(con.FORWARD_ASSESSMENT_REPORT_NAME, con.PDF_FILE_TYPE))
        createNotification("Reporte Descargado")

    } catch (err) {
        dispatch(setApplicationParameter({ 
            id: con.FORWARD_ASSESSMENT_REPORT, 
            value: { [con.GENERATING]: false, [con.REPORT_FILE_NAME]: null } 
        }))
        handleRequestError(err)
    }
}

export const makeOptionAssessmentReport = (assessmentSummary, assessedOptionArray) => async (dispatch, getState) => {
    
    dispatch(setApplicationParameter({ 
        id: con.OPTION_ASSESSMENT_REPORT, 
        value: { [con.GENERATING]: true, [con.REPORT_FILE_NAME]: null } 
    }))

    const applicationParameters = getState()[con.REDUCER_APPLICATION_PARAMETERS]

    let data = {}

    // Option Coverages (with assessment)
    data[con.REDUCER_OPTION_COVERAGES] = assessedOptionArray

    // Option Coverages Summary
    data[con.SUMMARY] = assessmentSummary

    // Adds Parameters
    data[con.PARAMETERS] = {
        [con.OPTION_ASSESSMENT_SHOW_EXPIRED_COVERAGES]: applicationParameters[con.OPTION_ASSESSMENT_SHOW_EXPIRED_COVERAGES],
        [con.OPTION_ASSESSMENT_REFERENCE_DATE]: applicationParameters[con.OPTION_ASSESSMENT_REFERENCE_DATE],
        [con.OPTION_ASSESSMENT_START_DATE]: applicationParameters[con.OPTION_ASSESSMENT_START_DATE],
        [con.USER]: surrogateActive() ? getSurrogateUser()[con.ID] : getUser()[con.ID]
    }

    const url = con.rest_finance_url + con.reports_finance + con.option_assessment_report_url
    const config = getFinanceRequestConfig()
    config.responseType = 'blob'

    try {
        const resp = await axios.post(url, data, config)
        dispatch(setApplicationParameter({ 
            id: con.OPTION_ASSESSMENT_REPORT, 
            value: { [con.GENERATING]: false } 
        }))
        downloadFileFromResponse(resp, constructFileName(con.FORWARD_ASSESSMENT_REPORT_NAME, con.PDF_FILE_TYPE))
        createNotification("Reporte Descargado")

    } catch (err) {
        dispatch(setApplicationParameter({ 
            id: con.OPTION_ASSESSMENT_REPORT, 
            value: { [con.GENERATING]: false, [con.REPORT_FILE_NAME]: null } 
        }))
        handleRequestError(err)
    }
}

// Method that gets the inputTemplate
export const downloadInputsTemplate = (clientType) =>
{

    const URL = con.rest_base_url + con.templates_api
    let fileName = con.INPUT_TEMPLATE_NAME_IMPORTER
    if(clientType === con.EXPORTER)
        fileName = con.INPUT_TEMPLATE_NAME_EXPORTER
     
    downloadFile(URL, fileName, {[con.TEMPLATE_NAME] : fileName })
}

export const constructFileName = (fileName, fileType) => {   
    const today = formatDate(getToday());
    const username = getVisualizedUser()?.[con.USERNAME] || "user";

    let extension = fileType.startsWith(".") ? fileType : `.${fileType}`;

    let response = `${today} ${fileName} ${username}${extension}`;

    if (fileType === con.XLSX_FILE_TYPE && response.length > con.MAX_EXCEL_SHEET_NAME) {
        response = `${today} ${fileName}`;
        response = response.substring(0, Math.min(response.length, con.MAX_EXCEL_SHEET_NAME - extension.length));
        response = `${response}${extension}`;
    }

    return response;
};

// Downloads generic file from server 
const downloadFile = (URL, fileName, data) =>
{
    axios.post(URL, data, {responseType: 'blob' })
        .then((resp)=>{
            downloadFileFromResponse(resp, fileName)
        })
        .catch(err => 
            handleRequestError(err)
        )
}



export const makeCommitteeReport = createAsyncThunk(
  "applicationParameters/makeCommitteeReport",
  async ({ startDate, endDate, bandsInfo, show_excel_coverages }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      
      let data = {};
      
      // Average Bands
      data[con.AVERAGE_FOR_PERCENTAGE] = bandsInfo[con.AVERAGE_FOR_PERCENTAGE];
      
      // Position data
      data[con.POSITION_IN_BANDS_DATA] = bandsInfo[con.POSITION_IN_BANDS_DATA];
      
      // Position Summary
      data[con.POSITION_IN_BANDS] = bandsInfo[con.POSITION_IN_BANDS];
      
      // Client Type
      data[con.CLIENT_TYPE] = state[con.REDUCER_MODEL_PARAMETERS][con.CLIENT_TYPE];
      data[con.USER_LINE] = state[con.REDUCER_MODEL_PARAMETERS][con.USER_LINE];
      data[con.TERM_IN_MONTHS] = state[con.REDUCER_MODEL_PARAMETERS][con.TERM_IN_MONTHS];

      // Start date
      data[con.START_DATE] = startDate;
      
      // Expired FWD
      data[con.SHOW_EXCEL_COVERAGES] = show_excel_coverages;
      
      // endDate
      data[con.END_DATE] = endDate;
      
      // Surrogate user logic
      let url;
      if (surrogateActive()) {
        data[con.SURROGATE_USER] = getSurrogateUser()[con.ID];
        url = con.rest_base_url + con.reports_api + con.STAFF_API + con.committee_report_url;
      } else {
        url = con.rest_base_url + con.reports_api + con.committee_report_url;
      }
      
      const config = getRequestConfig();
      config.responseType = 'blob';
      
      const resp = await axios.post(url, data, config);
    
      downloadBlobResponse(resp, (ext) =>
        constructFileName(con.COMMITTEE_REPORT, ext)
      );
      
      return resp.data;
      
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error generando el reporte de comité"));
    }
  }
);

export const makeGridLadderPptx = createAsyncThunk(
  "grid/makeGridLadderPptx",
  async ({ gridStrategy, clientType, weeksToWait, referenceValue }, { rejectWithValue }) => {
    try {
      const url = con.rest_finance_url + con.reports_finance + con.grid_ladder_pptx_url;

      const data = {
        [con.GRID_STRATEGY]: gridStrategy,
        [con.CLIENT_TYPE]: clientType,
        [con.WEEKS_TO_WAIT]: weeksToWait,
        [con.GRID_REFERENCE_VALUE]: referenceValue,
      };

      const config = getFinanceRequestConfig();
      config.responseType = 'blob';

      const resp = await axios.post(url, data, config);

      downloadBlobResponse(resp, (ext) =>
        constructFileName(con.GRID_LADDER_REPORT, ext)
      );

      return resp.data;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error generando la lámina de escalera"));
    }
  }
);

export const makeAllManagementReport = createAsyncThunk(
  "applicationParameters/makeAllManagementReport",
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      let data = {};
      
      // Start date
      data[con.START_DATE] = startDate;
      
      // endDate
      data[con.END_DATE] = endDate;
      
      // URL (siempre usa STAFF_API como en el original)
      const url = con.rest_base_url + con.reports_api + con.STAFF_API + con.all_management_report_url;
      
      const config = getRequestConfig();
      config.responseType = 'blob';
      
      const resp = await axios.post(url, data, config);
      
      // Descargar el archivo (usa XLSX_FILE_TYPE como en el original)
      downloadFileFromResponse(resp, constructFileName(con.ALL_MANAGEMENT_REPORT, con.XLSX_FILE_TYPE));
      
      return resp.data;
      
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error generando el reporte de gestión"));
    }
  }
);

export const makeMarketDataReport = createAsyncThunk(
  "applicationParameters/makeMarketDataReport",
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      let data = {};
      data[con.START_DATE] = startDate;
      data[con.END_DATE] = endDate;

      const url = con.rest_base_url + con.market_data_report_url;
      const config = getRequestConfig();
      config.responseType = 'blob';

      const resp = await axios.post(url, data, config);
      downloadBlobResponse(resp, (ext) => constructFileName(con.MARKET_DATA_REPORT, ext));

      return resp.data;

    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error generando el reporte de datos de mercado"));
    }
  }
);

function inferExtensionFromContentType(ct = "") {
  ct = ct.toLowerCase();
  if (ct.includes("application/zip")) return ".zip";
  if (ct.includes("application/vnd.openxmlformats-officedocument.presentationml.presentation")) return ".pptx";
  if (ct.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")) return ".xlsx";
  return ".bin";
}

function getFilenameFromDisposition(disposition) {
  if (!disposition) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
  if (star?.[1]) return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ""));
  const normal = /filename="?([^"]+)"?/i.exec(disposition);
  if (normal?.[1]) return normal[1].trim();
  return null;
}

function downloadBlobResponse(resp, fallbackNameMaker) {
  const contentType = resp.headers["content-type"] || "application/octet-stream";
  const disposition = resp.headers["content-disposition"];
  const serverName = getFilenameFromDisposition(disposition);

  // Blob
  const blob = new Blob([resp.data], { type: contentType });
  const url = URL.createObjectURL(blob);

  let filename = serverName;
  if (!filename) {
    const ext = inferExtensionFromContentType(contentType);
    filename = typeof fallbackNameMaker === "function"
      ? fallbackNameMaker(ext) 
      : "download" + ext;
  }

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

